// Initiative-type breakout metrics for the KPI dashboard / custom-report
// catalog.
//
// These metrics come from the ADSET media cube (`company_media_metrics_adsets`),
// queried grouped by the `initiative_type` dimension and pivoted on the frontend
// into wide one-row-per-date synthetic keys BEFORE the YoY merge (see
// `pivotInitiativeWindow` in apps/ui-rtk .../KPI/utils/initiative-rows.ts).
// Ad-set grain is deliberate: these breakouts report every classified ad set
// separately. `company_media_metrics_campaigns.initiative_type` exists too, but
// it is a lossy per-campaign summary of these same rows (APP-514).
// Two key families share the `initiatives` metric source:
//
//   initiative.<TYPE_VALUE>.<field>          — per initiative type
//   initiative_category.<CATEGORY>.<field>   — Demand Gen vs Demand Capture
//
// where <field> is one of INITIATIVE_METRIC_FIELDS (spend / modeled_revenue /
// modeled_brand_value). Predefined types (and the UNASSIGNED bucket) get
// static catalog entries via the builders below; a company's CUSTOM_* types
// are synthesized per-render by `initiativeTypeCatalogEntries` (mirroring
// `customMetricCatalogEntries`) because they are company-scoped and async.
//
// Category bucketing is DYNAMIC at pivot time: predefined types use the
// static category map here, custom types use their per-company category
// persisted in Postgres (threaded in from GET /custom-attribution/
// initiative-types), so Demand Gen / Demand Capture groupings follow whatever
// the company configures. Ad sets with no initiative type land in the
// UNASSIGNED type bucket and are excluded from both categories.

import type { MetricCatalogEntry } from './metric-catalog';
import type { MetricDefinition } from './types';

export const INITIATIVE_TYPE_KEY_PREFIX = 'initiative.';
export const INITIATIVE_CATEGORY_KEY_PREFIX = 'initiative_category.';

/** Sentinel bucket for ad sets whose `initiative_type` is NULL/empty. */
export const UNASSIGNED_INITIATIVE_TYPE = 'UNASSIGNED';

/** Picker category for every initiative-type breakout entry (static + synthesized). */
export const INITIATIVE_BREAKOUT_CATEGORY = 'Initiative Type Breakouts';

export type InitiativeCategoryValue = 'DEMAND_GENERATION' | 'DEMAND_CAPTURE';

export const INITIATIVE_CATEGORY_VALUES: InitiativeCategoryValue[] = ['DEMAND_GENERATION', 'DEMAND_CAPTURE'];

export const INITIATIVE_CATEGORY_LABELS: Record<InitiativeCategoryValue, string> = {
  DEMAND_GENERATION: 'Demand Gen',
  DEMAND_CAPTURE: 'Demand Capture',
};

/**
 * The measures pivoted per initiative type. `cubeMeasure` is the adset-cube
 * measure each field reads (the pivot maps cube columns onto synthetic keys).
 */
export const INITIATIVE_METRIC_FIELDS = [
  { field: 'spend', label: 'Spend', cubeMeasure: 'company_media_metrics_adsets.spend' },
  { field: 'modeled_revenue', label: 'Modeled Revenue', cubeMeasure: 'company_media_metrics_adsets.modeled_revenue' },
  {
    field: 'modeled_brand_value',
    label: 'Modeled Brand Value',
    cubeMeasure: 'company_media_metrics_adsets.estimated_d180_brand_value',
  },
] as const;

export type InitiativeMetricFieldName = (typeof INITIATIVE_METRIC_FIELDS)[number]['field'];

/**
 * Predefined initiative types (value / display label / DG-vs-DC category).
 * MUST stay in sync with `INITIATIVE_TYPE_LABELS` and
 * `INITIATIVE_TYPE_CATEGORIES` in apps/ui-rtk/src/constants/initiative-types.ts
 * (which keeps compile-time exhaustiveness against the InitiativeType enum) —
 * the "shared predefined initiative types stay in sync" spec there enforces it.
 */
export const PREDEFINED_INITIATIVE_TYPES: ReadonlyArray<{
  value: string;
  label: string;
  category: InitiativeCategoryValue;
}> = [
  { value: 'DEMAND_CAPTURE_ACQUISITION', label: 'Demand Capture: Cold Prospecting', category: 'DEMAND_CAPTURE' },
  { value: 'DEMAND_CAPTURE_RETARGETING', label: 'Demand Capture: Warm Prospecting', category: 'DEMAND_CAPTURE' },
  { value: 'DEMAND_CAPTURE_BROAD', label: 'Demand Capture: Lukewarm/Broad Prospecting', category: 'DEMAND_CAPTURE' },
  { value: 'DEMAND_GENERATION', label: 'Demand Generation: Engagement', category: 'DEMAND_GENERATION' },
  { value: 'DEMAND_GENERATION_2', label: 'Demand Generation: Traffic', category: 'DEMAND_GENERATION' },
  { value: 'RETENTION', label: 'Demand Capture: Retention', category: 'DEMAND_CAPTURE' },
  { value: 'BRANDED_SEARCH', label: 'Demand Capture: Branded Search', category: 'DEMAND_CAPTURE' },
  { value: 'NON_BRAND_SEARCH', label: 'Demand Capture: Non Brand Search', category: 'DEMAND_CAPTURE' },
  { value: 'PMAX', label: 'Demand Capture: PMAX', category: 'DEMAND_CAPTURE' },
];

export function initiativeTypeMetricKey(typeValue: string, field: InitiativeMetricFieldName): string {
  return `${INITIATIVE_TYPE_KEY_PREFIX}${typeValue}.${field}`;
}

export function initiativeCategoryMetricKey(category: InitiativeCategoryValue, field: InitiativeMetricFieldName): string {
  return `${INITIATIVE_CATEGORY_KEY_PREFIX}${category}.${field}`;
}

export function isInitiativeMetricKey(key: string): boolean {
  return key.startsWith(INITIATIVE_TYPE_KEY_PREFIX) || key.startsWith(INITIATIVE_CATEGORY_KEY_PREFIX);
}

/**
 * Static metrics for the "Demand Gen vs Demand Capture" catalog group:
 * spend / modeled revenue / modeled brand value per category. The category
 * membership of each initiative type resolves dynamically at pivot time, so
 * these six keys automatically follow per-company custom-type categories.
 */
export function buildInitiativeCategoryMetrics(): MetricDefinition[] {
  return INITIATIVE_CATEGORY_VALUES.flatMap((category) =>
    INITIATIVE_METRIC_FIELDS.map((field) => ({
      key: initiativeCategoryMetricKey(category, field.field),
      label: `${INITIATIVE_CATEGORY_LABELS[category]} ${field.label}`,
      type: 'currency' as const,
    })),
  );
}

/**
 * Static metrics for the "Initiative Type Breakouts" catalog group: every
 * predefined initiative type plus the Unassigned bucket, × spend / modeled
 * revenue / modeled brand value. A company's CUSTOM_* types are appended at
 * runtime via `initiativeTypeCatalogEntries`.
 */
export function buildInitiativeTypeMetrics(): MetricDefinition[] {
  const buckets = [...PREDEFINED_INITIATIVE_TYPES, { value: UNASSIGNED_INITIATIVE_TYPE, label: 'Unassigned' }];
  return buckets.flatMap((bucket) =>
    INITIATIVE_METRIC_FIELDS.map((field) => ({
      key: initiativeTypeMetricKey(bucket.value, field.field),
      label: `${bucket.label} ${field.label}`,
      type: 'currency' as const,
    })),
  );
}

/**
 * Minimal shape of a custom initiative type needed to synthesize catalog
 * entries — mirrors `CustomMetricLike`. `value` is the raw CUSTOM_* value
 * stored on ad sets; `label` is the user-facing display name.
 */
export interface InitiativeTypeLike {
  value: string;
  label: string;
}

const STATIC_INITIATIVE_TYPE_VALUES = new Set([
  ...PREDEFINED_INITIATIVE_TYPES.map((type) => type.value),
  UNASSIGNED_INITIATIVE_TYPE,
]);

/**
 * Synthesizes catalog entries for a company's custom initiative types. Like
 * `customMetricCatalogEntries`, these are company-scoped and async so they are
 * NEVER baked into the frozen METRIC_CATALOG singleton — callers merge them
 * per-render and thread them into resolveCatalogMetrics / the pickers.
 * Predefined values are skipped defensively so a duplicate can never shadow a
 * static catalog entry.
 */
export function initiativeTypeCatalogEntries(types: InitiativeTypeLike[]): MetricCatalogEntry[] {
  return types
    .filter((type) => !STATIC_INITIATIVE_TYPE_VALUES.has(type.value))
    .flatMap((type) =>
      INITIATIVE_METRIC_FIELDS.map((field) => ({
        key: initiativeTypeMetricKey(type.value, field.field),
        label: `${type.label} ${field.label}`,
        type: 'currency' as const,
        category: INITIATIVE_BREAKOUT_CATEGORY,
        source: 'initiatives' as const,
      })),
    );
}
